'use client';

import type { SearchResult } from 'conversational-search-client';
import { type JSX } from 'react';

import {
    CollapsibleContent,
    CollapsibleRoot,
    CollapsibleTrigger,
    IconButton,
    JustifiedGallery,
    Text,
} from '@videoblocks/design-system';
import { CollapseContent, ExpandContent } from '@videoblocks/design-system/icons';
import { Search } from '@videoblocks/react-icons';

import { useChatSessionContext } from '../../_context/SessionContext';
import { useTopResults } from '../../_hooks';
import { TopResultsSection } from './TopResultsSection';
import { VideoResultCard } from './VideoResultCard';
import { deriveAspectRatio } from './deriveAspectRatio';

interface VideoResultsGridProps {
    results: SearchResult[];
}

// The tray shows at most this many cited results in a 4-column grid (two rows max).
const MAX_TOP_RESULTS = 8;

export function VideoResultsGrid({ results }: VideoResultsGridProps): JSX.Element {
    const { showTopResults } = useChatSessionContext();
    const topResults = useTopResults()
        .filter((result) => result.contentClass !== 'audio')
        .slice(0, MAX_TOP_RESULTS);
    const hasTopResults = topResults.length > 0;
    // The tray is additive: cited items also remain in the full results grid below.
    const items = results.map((result) => ({
        ...result,
        id: result.contentId,
        aspectRatio: deriveAspectRatio(result.orientation),
    }));

    if (!showTopResults) {
        return (
            <div className="flex flex-col gap-3 p-4">
                <JustifiedGallery items={items} rowGapPx={8} columnGapPx={8}>
                    {({ item }) => <VideoResultCard result={item} />}
                </JustifiedGallery>
            </div>
        );
    }

    return (
        <CollapsibleRoot defaultOpen className="flex flex-col gap-2">
            {hasTopResults && (
                <CollapsibleContent>
                    <TopResultsSection results={topResults} />
                </CollapsibleContent>
            )}
            <section className="flex flex-col gap-2 px-6 py-2">
                <div className="flex items-center gap-2">
                    <Search className="size-4 shrink-0 text-gray-800" />
                    <Text asChild fontSize="base" fontWeight="semibold" className="text-gray-800">
                        <h2>All results</h2>
                    </Text>
                    <Text fontSize="xs" fontWeight="regular" className="text-gray-600">
                        {results.length} {results.length === 1 ? 'result' : 'results'}
                    </Text>
                    {hasTopResults && (
                        <CollapsibleTrigger asChild>
                            <IconButton
                                variant="transparent"
                                size="small"
                                aria-label="Toggle top results"
                                className="group ml-auto"
                                icon={
                                    <>
                                        <CollapseContent
                                            width={12}
                                            height={12}
                                            className="hidden group-data-[state=closed]:block"
                                        />
                                        <ExpandContent
                                            width={12}
                                            height={12}
                                            className="group-data-[state=closed]:hidden"
                                        />
                                    </>
                                }
                            />
                        </CollapsibleTrigger>
                    )}
                </div>
                <JustifiedGallery items={items} rowGapPx={8} columnGapPx={8}>
                    {({ item }) => <VideoResultCard result={item} />}
                </JustifiedGallery>
            </section>
        </CollapsibleRoot>
    );
}
